import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws, setActor } from '../lib/awsdb';
import { usePolling } from '../lib/poll';
import { storage } from '../lib/awsStorage';
import { authWrite } from '../lib/authWrite';
import { can } from '../lib/permissions';
import { formatMoney } from '../lib/money';
import { getOutstanding, getDisplayStatus, getDueBucket } from '../lib/invoiceCalc';
import { downloadXlsxSheets } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';
import InvoiceList from './InvoiceList';
import InvoiceDrawer from './InvoiceDrawer';
import PoDrawer from './PoDrawer';
import KpiStrip from './KpiStrip';
import InvoiceTabs from './InvoiceTabs';
import QuickPayPopover from './QuickPayPopover';
import OverflowMenu from './OverflowMenu';
import { logActivity } from '../lib/activityLog';
import { logPoActivity } from '../lib/poActivityLog';
import FilePreviewModal from './FilePreviewModal';
import ArchivePopup from './ArchivePopup';

export default function PoTracker({ session, onSignOut, embedded }) {
  // PoTracker shows BOTH PO and invoice rows; the action menu lets
  // staff create new POs and run invoice approve/dispute/pay flows.
  // We gate the "+ New PO" button with finance_pos_write and the
  // invoice action menu with finance_invoices_write.
  const canWritePos = can(session, 'finance_pos_write');
  const canWriteInvoices = can(session, 'finance_invoices_write');
  const [invoices, setInvoices] = useState([]);
  const [pos, setPos] = useState([]);
  const [lines, setLines] = useState([]);
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [catalog, setCatalog] = useState([]);

  const [search, setSearch] = useState('');
  const [kpiFilter, setKpiFilter] = useState(null);
  const [activeTab, setActiveTab] = useState('open');
  const [poSortAsc, setPoSortAsc] = useState(true); // Sort PO numbers ascending (true) or descending (false)

  function resetFilters() { setSearch(''); setKpiFilter(null); }
  const filtersActive = !!(search || kpiFilter);

  const [drawer, setDrawer] = useState(null);
  const [poDrawerOpen, setPoDrawerOpen] = useState(false);
  const [editPo, setEditPo] = useState(null);
  const [quickPay, setQuickPay] = useState(null);   // { invoice, anchorRect }
  const [overflow, setOverflow] = useState(null);   // { invoice, anchorRect }

  const [poLineItems, setPoLineItems] = useState([]);
  const [poActivity, setPoActivity] = useState([]);
  const [poAttachments, setPoAttachments] = useState([]);
  const [invoiceActivity, setInvoiceActivity] = useState([]);
  const [poComments, setPoComments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoiceAttachments, setInvoiceAttachments] = useState([]);

  const loadInvoices     = useCallback(async () => { const { data } = await aws.from('po_invoices').select('*').is('archived_at', null).not('po_id', 'is', null).order('created_at', { ascending: false }); setInvoices(data || []); }, []);
  const loadPos          = useCallback(async () => { const { data } = await aws.from('purchase_orders').select('id, po_number, supplier_id, supplier_name, currency, order_date, total_amount, notes, status, previous_status, locked, locked_by, tr_number, packing_slip_number, sales_order_number, sales_order_received_at, created_at, updated_at').is('archived_at', null).order('created_at', { ascending: false }); setPos(data || []); }, []);
  const loadLines        = useCallback(async () => { const { data } = await aws.from('po_invoice_line_items').select('id, invoice_id, po_line_item_id, qty_invoiced, po_line_items(qty_ordered)'); setLines(data || []); }, []);
  const loadPayments     = useCallback(async () => { const { data } = await aws.from('po_invoice_payments').select('id, invoice_id, amount, paid_at'); setPayments(data || []); }, []);
  const loadSuppliers    = useCallback(async () => { const { data } = await aws.from('suppliers').select('id, name').order('name', { ascending: true }); setSuppliers(data || []); }, []);
  const loadCatalog      = useCallback(async () => { const { data } = await aws.from('forecaster_catalog').select('*').neq('status', 'retired').order('sku_name'); setCatalog(data || []); }, []);
  const loadPoLineItems  = useCallback(async () => { const { data } = await aws.from('po_line_items').select('*'); setPoLineItems(data || []); }, []);
  const loadPoActivity   = useCallback(async () => { const { data } = await aws.from('po_activity_log').select('*').order('created_at', { ascending: false }); setPoActivity(data || []); }, []);
  const loadPoAttachments = useCallback(async () => { const { data } = await aws.from('po_attachments').select('*').order('created_at', { ascending: false }); setPoAttachments(data || []); }, []);
  const loadInvActivity  = useCallback(async () => { const { data } = await aws.from('invoice_activity_log').select('*').order('created_at', { ascending: false }); setInvoiceActivity(data || []); }, []);
  const loadPoComments   = useCallback(async () => { const { data } = await aws.from('po_comments').select('*').order('created_at', { ascending: false }); setPoComments(data || []); }, []);
  const loadPayMethods   = useCallback(async () => { const { data } = await aws.from('payment_methods').select('*').order('name'); setPaymentMethods(data || []); }, []);
  const loadInvAttach    = useCallback(async () => { const { data } = await aws.from('po_invoice_attachments').select('id, invoice_id, storage_path, filename, mime_type').order('uploaded_at', { ascending: false }); setInvoiceAttachments(data || []); }, []);

  const reloadAll = useCallback(() => {
    loadInvoices(); loadPos(); loadLines(); loadPayments(); loadSuppliers(); loadCatalog();
    loadPoLineItems(); loadPoActivity(); loadPoAttachments(); loadInvActivity(); loadPoComments(); loadPayMethods(); loadInvAttach();
  }, [loadInvoices, loadPos, loadLines, loadPayments, loadSuppliers, loadPoLineItems, loadPoActivity, loadPoAttachments, loadInvActivity, loadPoComments, loadPayMethods, loadInvAttach]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  usePolling([loadInvoices, loadPos, loadLines, loadPayments, loadSuppliers, loadPoLineItems, loadPoActivity, loadPoAttachments, loadInvActivity, loadPoComments, loadPayMethods, loadInvAttach]);

  const paidFor         = useCallback((id)  => payments.filter(p => p.invoice_id === id).reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);
  const outstandingFor  = useCallback((inv) => getOutstanding(inv, paidFor(inv.id)), [paidFor]);
  const displayStatusFor = useCallback((inv) => getDisplayStatus(inv, paidFor(inv.id)), [paidFor]);
  const isPaid = useCallback((inv) => displayStatusFor(inv) === 'paid', [displayStatusFor]);
  const hasVariance = useCallback((invoiceId) => {
    for (const l of lines) {
      if (l.invoice_id !== invoiceId) continue;
      if (!l.po_line_items) continue;
      if (Number(l.po_line_items.qty_ordered) !== Number(l.qty_invoiced)) return true;
    }
    return false;
  }, [lines]);

  // KPI amounts and counts — always computed from the full invoice set
  const kpis = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() + 7);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    const todayStr = today.toISOString().slice(0, 10);

    const acc = {
      due_now:        { amount: 0, count: 0 },
      due_this_week:  { amount: 0, count: 0 },
      overdue:        { amount: 0, count: 0 },
      disputed:       { amount: 0, count: 0 },
      drafts:         { amount: 0, count: 0 }
    };
    invoices.forEach(inv => {
      const paid = paidFor(inv.id);
      const ds = getDisplayStatus(inv, paid);
      const outstanding = getOutstanding(inv, paid);
      if (ds === 'paid' || outstanding <= 0.01) return;
      if (ds === 'disputed') { acc.disputed.amount += outstanding; acc.disputed.count++; return; }
      if (ds === 'draft') { acc.drafts.amount += outstanding; acc.drafts.count++; return; }
      // Approved or partial — assign to exactly one due bucket
      const bucket = getDueBucket(inv, paid);
      if (bucket === 'overdue') { acc.overdue.amount += outstanding; acc.overdue.count++; }
      else if (bucket === 'due_this_week') { acc.due_this_week.amount += outstanding; acc.due_this_week.count++; }
      else { acc.due_now.amount += outstanding; acc.due_now.count++; }
    });
    return acc;
  }, [invoices, isPaid, outstandingFor]);



  // PO ids in each tab bucket
  const openPoIds = useMemo(() => new Set(pos.filter(p => p.status !== 'closed').map(p => p.id)), [pos]);
  const closedPoIds = useMemo(() => new Set(pos.filter(p => p.status === 'closed').map(p => p.id)), [pos]);

  // Filter invoices for the active tab
  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    const todayStr = new Date().toISOString().slice(0, 10);
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() + 7);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    const tabPoIds = activeTab === 'closed' ? closedPoIds : openPoIds;

    let list = invoices.filter(inv => {
      // Tab filter — only show invoices whose PO is in the right bucket
      if (!tabPoIds.has(inv.po_id)) return false;
      if (q) {
        const hit = (inv.invoice_number || '').toLowerCase().includes(q)
          || (inv.po_number_text || '').toLowerCase().includes(q)
          || (inv.notes || '').toLowerCase().includes(q)
          || (inv.supplier_name || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (kpiFilter) {
        if (isPaid(inv)) return false;
        const outstanding = Math.max(0, outstandingFor(inv));
        if (outstanding <= 0.01) return false;
        if (kpiFilter === 'due_now' && inv.status !== 'approved') return false;
        if (kpiFilter === 'due_this_week' && !(inv.status === 'approved' && inv.due_date && inv.due_date <= cutoffStr)) return false;
        if (kpiFilter === 'overdue' && !(inv.status === 'approved' && inv.due_date && inv.due_date < todayStr)) return false;
        if (kpiFilter === 'disputed' && inv.status !== 'disputed') return false;
        if (kpiFilter === 'drafts' && inv.status !== 'draft') return false;
      }
      return true;
    });

    // Sort: biggest outstanding first
    list.sort((a, b) => outstandingFor(b) - outstandingFor(a));
    return list;
  }, [invoices, search, kpiFilter, activeTab, openPoIds, closedPoIds, isPaid, outstandingFor]);

  // Tab counts: POs (not invoices)
  const tabCounts = useMemo(() => ({
    open: pos.filter(p => p.status !== 'closed').length,
    closed: pos.filter(p => p.status === 'closed').length,
  }), [pos]);

  async function setStatus(inv, nextStatus, activity) {
    try {
      await authWrite(session, 'po_invoices', 'update', { status: nextStatus }, { column: 'id', value: inv.id });
    } catch (err) { alert('Failed: ' + err.message); return; }
    await logActivity(inv.id, session, activity);
    loadInvoices(); loadInvActivity();
  }

  async function resolveDispute(inv) {
    if (!confirm(`Resolve dispute on ${inv.invoice_number || 'this invoice'}? Returns to Draft.`)) return;
    await setStatus(inv, 'draft', {
      action_type: 'status_change', before_value: 'disputed', after_value: 'draft'
    });
  }

  async function reopenInvoice(inv) {
    if (!confirm(`Reopen ${inv.invoice_number || 'this invoice'}? It will return to Approved status.`)) return;
    try {
      await authWrite(session, 'po_invoices', 'update', { status: 'approved' }, { column: 'id', value: inv.id });
    } catch (err) { alert('Failed: ' + err.message); return; }
    await logActivity(inv.id, session, {
      action_type: 'status_change', before_value: 'paid', after_value: 'approved'
    });
    loadInvoices(); loadInvActivity();
  }

  async function backToDraft(inv) {
    if (!confirm(`Return ${inv.invoice_number || 'this invoice'} to Draft?`)) return;
    await setStatus(inv, 'draft', {
      action_type: 'status_change', before_value: inv.status, after_value: 'draft'
    });
  }

  // Approve modal state
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveNote, setApproveNote] = useState('');
  const [approveAmount, setApproveAmount] = useState('');
  const [approveFile, setApproveFile] = useState(null);

  function openApproveModal(inv) {
    const paid = paidFor(inv.id);
    const ost = Math.max(0, Number(inv.total || 0) - paid);
    setApproveTarget(inv);
    setApproveNote('');
    setApproveAmount(ost > 0 ? ost.toFixed(2) : '');
    setApproveFile(null);
  }

  async function confirmApprove() {
    if (!approveTarget || !approveNote.trim()) return;
    const amt = parseFloat(approveAmount) || 0;
    try {
      await authWrite(session, 'po_invoices', 'update', { status: 'approved', amount_approved: amt > 0 ? amt : null }, { column: 'id', value: approveTarget.id });
    } catch (err) { alert('Failed: ' + err.message); return; }
    let approveProofPath = null;
    if (approveFile) {
      const safeName = approveFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const key = `invoice/${approveTarget.id}/${Date.now()}-${safeName}`;
      const { error: upErr, data: upData } = await storage.from('po-attachments').upload(key, approveFile, { contentType: approveFile.type, upsert: false });
      if (!upErr) {
        approveProofPath = upData?.path || key;
        await authWrite(session, 'po_invoice_attachments', 'insert', {
          invoice_id: approveTarget.id, storage_path: approveProofPath, filename: approveFile.name,
          mime_type: approveFile.type, size_bytes: approveFile.size, uploaded_by: session.code,
        });
      } else {
        approveProofPath = null;
      }
    }
    const approveRef = `APR-${approveTarget.id.slice(0, 6).toUpperCase()}`;
    await logActivity(approveTarget.id, session, {
      action_type: 'status_change', before_value: approveTarget.status, after_value: 'approved',
      notes: `detail:${JSON.stringify({
        ref: approveRef, type: 'approval', amount: amt, reason: approveNote.trim(),
        file: approveFile?.name || null,
        file_path: approveProofPath, file_bucket: approveProofPath ? 'po-attachments' : null
      })}`
    });
    loadInvoices(); loadInvActivity(); loadInvAttach();
    setApproveTarget(null); setApproveNote(''); setApproveAmount(''); setApproveFile(null);
  }

  // Dispute modal state
  const [disputeTarget, setDisputeTarget] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');

  async function confirmDispute() {
    if (!disputeTarget || !disputeReason.trim()) return;
    try {
      await authWrite(session, 'po_invoices', 'update', { status: 'disputed', dispute_reason: disputeReason.trim() }, { column: 'id', value: disputeTarget.id });
    } catch (err) { alert('Failed: ' + err.message); return; }
    await logActivity(disputeTarget.id, session, {
      action_type: 'marked_disputed',
      before_value: disputeTarget.status,
      after_value: 'disputed',
      notes: disputeReason.trim()
    });
    loadInvoices(); loadInvActivity();
    setDisputeTarget(null); setDisputeReason('');
  }

  async function deleteInvoice(inv) {
    setArchiveTarget(inv);
    return;
  }
  async function confirmArchiveInvoice(reason) {
    const inv = archiveTarget;
    if (!inv) return;
    try {
      await authWrite(session, 'po_invoices', 'update', { archived_at: new Date().toISOString(), archived_by: session.name }, { column: 'id', value: inv.id });
    } catch (err) { alert('Failed: ' + err.message); }
    if (inv.po_id) await logPoActivity(inv.po_id, session, 'Archived invoice', `${inv.invoice_number || ''}${reason ? ' — ' + reason : ''}`);
    setArchiveTarget(null);
    loadInvoices(); loadLines(); loadPayments(); loadInvActivity(); loadPoActivity();
  }

  // POs that have no invoices — filtered by tab and search
  const displayedOrphanPos = useMemo(() => {
    const idsWithInvoices = new Set(invoices.map(i => i.po_id).filter(Boolean));
    const tabPoIds = activeTab === 'closed' ? closedPoIds : openPoIds;
    let list = pos.filter(p => !idsWithInvoices.has(p.id) && tabPoIds.has(p.id));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(p =>
        (p.po_number || '').toLowerCase().includes(q) ||
        (p.supplier_name || '').toLowerCase().includes(q)
      );
    }
    // Sort numerically by PO number (extract digits and compare)
    list.sort((a, b) => {
      const aNum = parseInt((a.po_number || '').replace(/\D/g, '')) || 0;
      const bNum = parseInt((b.po_number || '').replace(/\D/g, '')) || 0;
      return poSortAsc ? aNum - bNum : bNum - aNum;
    });
    return list;
  }, [pos, invoices, activeTab, openPoIds, closedPoIds, search, poSortAsc]);

  // File preview state (for dashboard eye icons)
  const [filePreview, setFilePreview] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  async function previewInvoiceFile(att) {
    const { data, error } = await storage.from('po-attachments').createSignedUrl(att.storage_path, 3600);
    if (error || !data?.signedUrl) { alert('Could not open file'); return; }
    setFilePreview({ url: data.signedUrl, filename: att.filename });
  }

  const actions = {
    onOpenDrawer: (inv) => setDrawer({ invoice: inv }),
    onActions: (inv, anchorRect) => setOverflow({ invoice: inv, anchorRect }),
    onEditPo: (po) => setEditPo(po),
    onLogInvoiceForPo: (po) => setDrawer({ invoice: null, prefillPoId: po.id }),
    onOpenActivity: (inv) => setDrawer({ invoice: inv, defaultTab: 'activity' }),
    onPreviewFile: (att) => previewInvoiceFile(att)
  };

  function handleDownload() {
    const poById = {};
    pos.forEach(p => { poById[p.id] = p; });
    const invoiceColumns = [
      { label: 'PO Number', width: 14, value: r => poById[r.po_id]?.po_number || r.po_number_text || '' },
      { label: 'Supplier', width: 22, value: r => r.supplier_name || poById[r.po_id]?.supplier_name || '' },
      { label: 'Invoice Number', width: 16, value: r => r.invoice_number || '' },
      { label: 'Order Date', width: 14, value: r => poById[r.po_id]?.order_date || '' },
      { label: 'Due Date', width: 14, value: r => r.due_date || '' },
      { label: 'Currency', width: 10, value: r => r.currency || poById[r.po_id]?.currency || '' },
      { label: 'Total', width: 14, numFmt: '#,##0.00', value: r => Number(r.total || 0) },
      { label: 'Paid', width: 14, numFmt: '#,##0.00', value: r => Number(paidFor(r.id) || 0) },
      { label: 'Outstanding', width: 14, numFmt: '#,##0.00', value: r => Math.max(0, outstandingFor(r)) },
      { label: 'Status', width: 14, value: r => r.status || '' },
    ];
    const poColumns = [
      { label: 'PO Number', width: 14, value: r => r.po_number || '' },
      { label: 'Supplier', width: 22, value: r => r.supplier_name || '' },
      { label: 'Order Date', width: 14, value: r => r.order_date || '' },
      { label: 'Currency', width: 10, value: r => r.currency || '' },
      { label: 'Total', width: 14, numFmt: '#,##0.00', value: r => Number(r.total_amount || 0) },
      { label: 'Status', width: 14, value: r => r.status || '' },
    ];
    const filename = `FON_PO_Tracker_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsxSheets(filename, [
      { name: 'Invoices', columns: invoiceColumns, rows: displayed },
      { name: 'Open POs (no invoice)', columns: poColumns, rows: displayedOrphanPos },
    ]);
    logExportActivity(session, 'PO Tracker', filename);
  }

  function selectKpi(key) {
    setKpiFilter(key);
    if (key && activeTab === 'closed') setActiveTab('open');
  }

  return (
    <div className={embedded ? '' : 'app-screen'}>

      <div className={embedded ? '' : 'main-content'}>
        <KpiStrip kpis={kpis} activeKey={kpiFilter} onSelect={selectKpi} />

        <div className="toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="Search PO, invoice, supplier..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {filtersActive && (
            <button type="button" className="filter-reset" onClick={resetFilters}>Reset filters</button>
          )}
          <button type="button" className="filter-reset" onClick={() => setPoSortAsc(!poSortAsc)} title="Toggle PO sort order" style={{ fontWeight: 600 }}>
            Sort: {poSortAsc ? 'ASC ↑' : 'DESC ↓'}
          </button>
          <div className="toolbar-spacer" />
          {(canWritePos || canWriteInvoices) && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
          {canWritePos && <button className="btn btn-primary" onClick={() => setPoDrawerOpen(true)}>+ New PO</button>}
        </div>

        <InvoiceTabs active={activeTab} counts={tabCounts} onChange={setActiveTab} />

        <InvoiceList
          invoices={displayed}
          pos={pos}
          orphanPos={displayedOrphanPos}
          sortAsc={poSortAsc}
          mode="active"
          outstandingFor={outstandingFor}
          paidFor={paidFor}
          hasVariance={hasVariance}
          actions={actions}
          invoiceAttachments={invoiceAttachments}
          poAttachments={poAttachments}
        />
      </div>

      {drawer && (
        <InvoiceDrawer
          session={session}
          invoice={drawer.invoice}
          prefillPoId={drawer.prefillPoId}
          defaultTab={drawer.defaultTab}
          pos={pos}
          suppliers={suppliers}
          catalog={catalog}
          onClose={() => setDrawer(null)}
          onSaved={() => { loadInvoices(); loadLines(); loadSuppliers(); loadInvActivity(); loadPoActivity(); }}
        />
      )}

      {poDrawerOpen && (
        <PoDrawer
          session={session}
          existingPoNumbers={pos.map(p => p.po_number)}
          suppliers={suppliers}
          onClose={() => setPoDrawerOpen(false)}
          onSaved={() => { loadPos(); loadSuppliers(); loadPoLineItems(); loadPoActivity(); }}
        />
      )}

      {editPo && (() => {
        const linked = invoices.filter(i => i.po_id === editPo.id);
        const poBilled = linked.reduce((s, inv) => s + Number(inv.total || 0), 0);
        const poPaid = linked.reduce((s, inv) => s + paidFor(inv.id), 0);
        return (
          <PoDrawer
            session={session}
            po={editPo}
            existingPoNumbers={pos.map(p => p.po_number)}
            suppliers={suppliers}
            onClose={() => setEditPo(null)}
            onSaved={() => { loadPos(); loadSuppliers(); loadInvoices(); loadPoLineItems(); loadPoActivity(); loadPoAttachments(); loadPoComments(); }}
            onDeleted={() => { loadPos(); loadInvoices(); loadLines(); loadPayments(); loadPoLineItems(); loadPoActivity(); loadPoAttachments(); }}
            onPoStatusChanged={(newStatus) => {
              // Auto-flip the tab to where the PO just landed so Mo
              // sees the result of his action instead of an empty list.
              setActiveTab(newStatus === 'closed' ? 'closed' : 'open');
            }}
            poLineItems={poLineItems.filter(l => l.po_id === editPo.id)}
            poActivity={poActivity.filter(a => a.po_id === editPo.id)}
            invoiceActivity={invoiceActivity.filter(a => {
              const invIds = new Set(linked.map(i => i.id));
              return invIds.has(a.invoice_id);
            })}
            poComments={poComments.filter(c => c.po_id === editPo.id)}
            invoiceLines={lines.filter(l => {
              const invIds = new Set(linked.map(i => i.id));
              return invIds.has(l.invoice_id);
            })}
            linkedInvoices={linked}
            poBilled={poBilled}
            poPaid={poPaid}
          />
        );
      })()}

      {quickPay && (
        <QuickPayPopover
          invoice={quickPay.invoice}
          outstanding={Math.max(0, outstandingFor(quickPay.invoice))}
          session={session}
          paymentMethods={paymentMethods}
          onClose={() => setQuickPay(null)}
          onSaved={() => { loadPayments(); loadInvoices(); loadInvActivity(); loadPayMethods(); }}
        />
      )}

      {overflow && (() => {
        const inv = overflow.invoice;
        const st = inv.status;
        const autoPaid = Number(inv.total) > 0 && outstandingFor(inv) <= 0.01;
        const effectiveStatus = autoPaid ? 'paid' : st;
        const items = [
          { key: 'log_payment', label: 'Log Payment', disabled: effectiveStatus === 'disputed' || effectiveStatus === 'draft', onClick: () => setQuickPay({ invoice: inv }) },
        ];
        if (effectiveStatus === 'draft') {
          items.push({ key: 'approve', label: 'Approve', onClick: () => openApproveModal(inv) });
          items.push({ key: 'dispute', label: 'Dispute', onClick: () => { setDisputeTarget(inv); setDisputeReason(''); } });
        } else if (effectiveStatus === 'approved') {
          items.push({ key: 'dispute', label: 'Dispute', onClick: () => { setDisputeTarget(inv); setDisputeReason(''); } });
          items.push({ key: 'back_draft', label: 'Back to Draft', onClick: () => backToDraft(inv) });
        } else if (effectiveStatus === 'disputed') {
          items.push({ key: 'resolve', label: 'Resolve', onClick: () => resolveDispute(inv) });
        } else if (effectiveStatus === 'paid') {
          items.push({ key: 'reopen', label: 'Reopen Invoice', onClick: () => reopenInvoice(inv) });
          items.push({ key: 'dispute', label: 'Dispute', onClick: () => { setDisputeTarget(inv); setDisputeReason(''); } });
        }
        items.push({ key: 'view', label: 'View Invoice', onClick: () => setDrawer({ invoice: inv }) });
        items.push({ key: 'delete', label: 'Delete', danger: true, onClick: () => deleteInvoice(inv) });
        return (
          <OverflowMenu anchorRect={overflow.anchorRect} onClose={() => setOverflow(null)} items={items} />
        );
      })()}

      {/* Dispute reason modal */}
      {disputeTarget && createPortal(
        <>
          <div className="quickpay-backdrop" onClick={() => setDisputeTarget(null)} />
          <div className="quickpay-modal" role="dialog" aria-label="Dispute invoice">
            <div className="quickpay-title">
              <span>Dispute Invoice</span>
              <span className="quickpay-invoice">{disputeTarget.invoice_number}</span>
            </div>
            <div className="quickpay-row">
              <label>Reason for dispute<span className="required-star">*</span></label>
              <textarea
                style={{ width: '100%', minHeight: 80, padding: '8px 10px', border: '1px solid var(--border-input)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', fontSize: 'var(--text-sm)', resize: 'vertical' }}
                value={disputeReason}
                onChange={e => setDisputeReason(e.target.value)}
                placeholder="Describe the issue..."
                autoFocus
              />
            </div>
            <div className="quickpay-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setDisputeTarget(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={!disputeReason.trim()} onClick={confirmDispute}>Confirm Dispute</button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Approve modal */}
      {approveTarget && createPortal(
        <>
          <div className="quickpay-backdrop" onClick={() => setApproveTarget(null)} />
          <div className="quickpay-modal" role="dialog" aria-label="Approve invoice">
            <div className="quickpay-title">
              <span>Approve Invoice</span>
              <span className="quickpay-invoice">{approveTarget.invoice_number}</span>
            </div>
            <div className="quickpay-row">
              <label>Total Owing</label>
              <div className="readonly-value" style={{ fontSize: 14 }}>{formatMoney(approveTarget.total || 0)}</div>
            </div>
            <div className="quickpay-row">
              <label>Paid to Date</label>
              <div className="readonly-value" style={{ fontSize: 14, color: '#036e43' }}>{formatMoney(paidFor(approveTarget.id))}</div>
            </div>
            <div className="quickpay-row">
              <label>Amount Approved</label>
              <input type="number" step="0.01" value={approveAmount} onChange={e => setApproveAmount(e.target.value)} placeholder="0.00" />
            </div>
            <div className="quickpay-row">
              <label>Reason for Approval<span className="required-star">*</span></label>
              <textarea
                style={{ width: '100%', minHeight: 80, padding: '8px 10px', border: '1px solid var(--border-input)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', fontSize: 'var(--text-sm)', resize: 'vertical' }}
                value={approveNote}
                onChange={e => setApproveNote(e.target.value)}
                placeholder="Why is this approved for payment?"
                autoFocus
              />
            </div>
            <div className="quickpay-row">
              <label>Attach file <span className="muted-xs">(optional)</span></label>
              <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={e => setApproveFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
            </div>
            <div className="quickpay-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => { setApproveTarget(null); setApproveFile(null); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={!approveNote.trim()} onClick={confirmApprove}>Confirm</button>
            </div>
          </div>
        </>,
        document.body
      )}


      {filePreview && <FilePreviewModal url={filePreview.url} filename={filePreview.filename} onClose={() => setFilePreview(null)} />}
      {archiveTarget && <ArchivePopup itemName={archiveTarget.invoice_number || 'this invoice'} onClose={() => setArchiveTarget(null)} onConfirm={confirmArchiveInvoice} />}
    </div>
  );
}
